import { FC } from 'react';
import { Button, Menu } from 'react-daisyui';
import { cn } from '@/utils/classNames.ts';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { useTranslation } from 'react-i18next';
import { navData } from '@/layouts/Default/config-nav.tsx';
import { usePathname } from '@/hooks/usePathname.ts';
import { Link } from 'react-router-dom';
import { ArrowLeftStartOnRectangleIcon } from '@heroicons/react/24/solid';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import Routes from '@/router/Routes.ts';

type NavbarProps = {
  onClick?: () => void;
  menuClassName?: string;
};

const Navbar: FC<PropsWithClassName<NavbarProps>> = ({ className, onClick, menuClassName }) => {
  const {
    logoutQuery: { mutateAsync: logout, isPending: isLoadingLogout },
  } = useAuthContext();
  const { t } = useTranslation();
  const pathname = usePathname();

  const handleLogout = () => {
    void (async () => {
      try {
        if (isLoadingLogout) return;
        await logout();
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <nav className={cn('flex flex-col gap-4 justify-between grow', className)}>
      <div className="flex flex-col grow gap-6">
        <Link to={Routes.DASHBOARD} onClick={onClick ? onClick : undefined}>
          <Button className="w-full" color="primary">
            {t('button.return_to_chat')}
          </Button>
        </Link>
        <Menu className={cn('p-0 overflow-auto flex-nowrap grow h-1', menuClassName)}>
          {navData.map((item) => {
            const isActivated = item.path === pathname;

            return (
              <Menu.Item key={item.path}>
                <Link
                  to={item.path}
                  title={t(item.title)}
                  onClick={() => (onClick ? onClick() : null)}
                  className={cn('active:bg-primary-400', {
                    'bg-secondary-400 text-white': isActivated,
                  })}
                >
                  {item.icon ? <div className="w-4 h-4">{item.icon}</div> : null}
                  <span className="text-ellipsis line-clamp-1">{t(item.title)}</span>
                </Link>
              </Menu.Item>
            );
          })}
        </Menu>
      </div>
      <div className="flex flex-col">
        <LoadingButton
          loading={isLoadingLogout}
          onClick={handleLogout}
          color="primary"
          startIcon={<ArrowLeftStartOnRectangleIcon className="w-4" />}
        >
          {t('button.logout')}
        </LoadingButton>
      </div>
    </nav>
  );
};

export default Navbar;
